import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  Leaf, Brain, FileSearch, Clock3, Stethoscope, FileText, Activity, FolderOpen, BellRing, ShieldCheck,
  ArrowRight, CheckCircle2, CalendarCheck, Users, ScanSearch, Archive, Loader2,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import SiteFooter from "@/components/SiteFooter";
import api from "@/lib/api";

const FEATURES = [
  { icon: Brain, title: "AI Patient Summary", desc: "Overall health score, risk level and clinical alerts generated from recorded data." },
  { icon: FileSearch, title: "AI Report Analysis", desc: "Extracts real values from uploaded reports with source and confidence labels." },
  { icon: Clock3, title: "AI Patient Timeline", desc: "Chronological view of diagnosis, medicines, procedures, and visits." },
  { icon: Stethoscope, title: "Consultation Assistant", desc: "A patient brief before every consultation — never a diagnosis." },
  { icon: FileText, title: "Digital Prescription", desc: "Write, save and print clean, structured prescriptions." },
  { icon: Activity, title: "Vitals & Trends", desc: "Specialty-specific vitals with weekly, monthly and quarterly trends." },
  { icon: FolderOpen, title: "Report Vault", desc: "Every report, prescription and note in one searchable place." },
  { icon: BellRing, title: "Follow-up Intelligence", desc: "Surfaces missed follow-ups and high-risk patients automatically." },
  { icon: ShieldCheck, title: "Clinical Monitoring", desc: "Specialty-driven monitoring so nothing important is overlooked." },
];

const STEPS = ["Create Clinic", "Register Patient", "Upload Reports", "AI Assists Doctor"];

const BENEFITS = [
  "Saves consultation time",
  "Better patient history",
  "Faster report review",
  "Chronic disease tracking",
  "AI-powered insights",
  "Secure cloud platform",
];

const CORE_FEATURES = [
  { icon: Users, title: "Staff Account Management", desc: "Empowers doctors to easily create, edit, activate, or deactivate dedicated receptionist and staff profiles within their tier limit." },
  { icon: Brain, title: "AI Patient Summary", desc: "Generates clear overall health scores, clinical alerts, risk levels, and identifies missing investigations from factual history." },
  { icon: Clock3, title: "AI Chronological Timeline", desc: "Automatically maps out every patient diagnosis, medication change, lab result, and hospital visit onto a visual timeline." },
  { icon: FileSearch, title: "AI Lab Report Analyzer (OCR)", desc: "Extracts values from uploaded PDFs and images asynchronously with built-in confidence indicators and source verification tracking." },
  { icon: Stethoscope, title: "AI Consultation Assistant", desc: "Delivers a comprehensive patient brief prior to checking the patient in, highlighting core concerns and recent laboratory trends." },
  { icon: FileText, title: "Dynamic Digital Prescription Writer", desc: "Creates structured prescriptions complete with lifestyle guidance, medical disclaimers, and instant React-PDF downloading." },
  { icon: Activity, title: "Vitals & Trend Analytics", desc: "Multi-parameter trends over weekly, monthly, or quarterly cycles specific to your speciality." },
  { icon: ShieldCheck, title: "Secure Multi-Tenant Vault", desc: "Private, path-isolated storage for clinical uploads with strict JWT validation ensuring absolute data privacy between clinics." },
  { icon: ScanSearch, title: "Duplicate Patient Detection", desc: "Front-desk scanning that checks name, phone, and date of birth instantly upon input to eliminate redundant data entries." },
  { icon: Archive, title: "Data Lifecycle Archiving", desc: "Soft-delete mechanisms that allow doctors to clean up active patient lists while legally retaining background records in compliance with medical data regulations." },
];

export default function LandingPage() {
  const [plans, setPlans] = useState([]);
  const [plansLoading, setPlansLoading] = useState(true);

  useEffect(() => {
    if (window.location.hash) {
      const el = document.querySelector(window.location.hash);
      if (el) el.scrollIntoView({ behavior: "smooth" });
    }
  }, []);

  useEffect(() => {
    api
      .get("/public/plans")
      .then(({ data }) => setPlans(data))
      .finally(() => setPlansLoading(false));
  }, []);

  return (
    <div className="bg-slate-50 text-slate-900">
      <header className="sticky top-0 z-40 border-b border-slate-200 bg-white/90 backdrop-blur-sm">
        <div className="mx-auto flex max-w-6xl items-center justify-between px-4 py-4 sm:px-6">
          <div className="flex items-center gap-2">
            <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-teal-700 text-white">
              <Leaf className="h-5 w-5" />
            </div>
            <span className="text-lg font-semibold text-slate-900">RootLyst</span>
          </div>
          <nav className="hidden items-center gap-8 sm:flex">
            <a data-testid="navbar-features-link" href="#features" className="text-sm font-medium text-slate-600 transition-colors hover:text-teal-700">
              Features
            </a>
            <a data-testid="navbar-pricing-link" href="#pricing" className="text-sm font-medium text-slate-600 transition-colors hover:text-teal-700">
              Pricing
            </a>
          </nav>
          <Link to="/contact-sales">
            <Button data-testid="navbar-book-demo-btn" className="bg-teal-700 hover:bg-teal-800">Book Demo</Button>
          </Link>
        </div>
      </header>

      <section className="mx-auto max-w-6xl px-4 py-16 sm:px-6 sm:py-24">
        <div className="grid grid-cols-1 items-center gap-12 lg:grid-cols-2">
          <div>
            <span className="inline-flex items-center rounded-full bg-teal-50 px-3 py-1 text-xs font-medium text-teal-700">
              Built for Specialist Clinics
            </span>
            <h1 className="mt-4 text-4xl font-semibold leading-tight text-slate-900 sm:text-5xl lg:text-6xl" data-testid="hero-heading">
              AI Clinical Assistant for Modern Specialist Clinics
            </h1>
            <p className="mt-5 text-base leading-relaxed text-slate-600 sm:text-lg">
              RootLyst uses AI and helps specialists organize patient history, analyze lab reports, monitor disease
              progression, and deliver faster, data-driven care — all from one intelligent platform.
            </p>
            <div className="mt-8 flex flex-wrap gap-3">
              <Link to="/contact-sales">
                <Button data-testid="hero-book-demo-btn" size="lg" className="gap-2 bg-teal-700 hover:bg-teal-800">
                  Book Demo <ArrowRight className="h-4 w-4" />
                </Button>
              </Link>
            </div>
          </div>
          <div className="relative">
            <div className="overflow-hidden rounded-2xl border border-slate-200 shadow-sm">
              <img
                src="https://images.unsplash.com/photo-1758691462848-31a39258dbd8?crop=entropy&cs=srgb&fm=jpg&q=85"
                alt="Doctor using RootLyst dashboard"
                className="h-full w-full object-cover"
              />
            </div>
          </div>
        </div>
      </section>

      <section id="features" className="mx-auto max-w-6xl px-4 py-16 sm:px-6 scroll-mt-20">
        <h2 className="text-center text-3xl font-semibold text-slate-900">Everything a specialist clinic needs</h2>
        <div className="mt-10 grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3">
          {FEATURES.map((f) => (
            <div key={f.title} className="card-surface card-surface-hover rounded-xl border border-slate-200 bg-white p-6 shadow-sm transition-transform hover:-translate-y-1" data-testid={`feature-card-${f.title.replace(/\s/g, "-")}`}>
              <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-teal-50 text-teal-700">
                <f.icon className="h-5 w-5" />
              </div>
              <h3 className="mt-4 font-semibold text-slate-900">{f.title}</h3>
              <p className="mt-2 text-sm text-slate-600">{f.desc}</p>
            </div>
          ))}
        </div>
      </section>

      <section className="bg-white py-16">
        <div className="mx-auto max-w-4xl px-4 sm:px-6">
          <h2 className="text-center text-3xl font-semibold text-slate-900">How It Works</h2>
          <div className="mx-auto mt-10 max-w-sm">
            {STEPS.map((step, i) => (
              <div key={step} className="relative flex items-center gap-4 pb-10 last:pb-0">
                {i < STEPS.length - 1 && (
                  <span className="absolute left-5 top-10 h-[calc(100%-2.5rem)] w-px -translate-x-1/2 bg-slate-300" />
                )}
                <span className="relative z-10 flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-teal-700 text-sm font-semibold leading-none text-white">
                  {i + 1}
                </span>
                <p className="text-lg font-medium text-slate-900">{step}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="mx-auto max-w-6xl px-4 py-16 sm:px-6">
        <h2 className="text-3xl font-semibold text-slate-900">Why doctors choose RootLyst</h2>
        <div className="mt-8 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {BENEFITS.map((b) => (
            <div key={b} className="flex items-center gap-3 card-surface card-surface-hover rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
              <CheckCircle2 className="h-5 w-5 shrink-0 text-green-600" />
              <span className="text-slate-800">{b}</span>
            </div>
          ))}
        </div>
      </section>

      <section id="pricing" className="bg-white py-16 scroll-mt-20">
        <div className="mx-auto max-w-6xl px-4 sm:px-6">
          <h2 className="text-center text-3xl font-semibold text-slate-900">Subscription Plans</h2>
          <p className="mx-auto mt-3 max-w-2xl text-center text-sm text-slate-600">
            Every tier gets the full RootLyst platform — plans only differ by appointment volume and staff seats.
          </p>
          <div className="mt-10 grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-4">
            {plansLoading ? (
              <div className="col-span-full flex justify-center py-10">
                <Loader2 className="h-6 w-6 animate-spin text-slate-400" />
              </div>
            ) : (
              plans.map((p) => {
                const highlight = p.plan_code === "PRO";
                const isFree = Number(p.price_monthly) === 0;
                return (
                  <div
                    key={p.plan_code}
                    data-testid={`pricing-card-${p.plan_code}`}
                    className={`card-surface flex flex-col rounded-xl p-6 shadow-sm transition-[transform,box-shadow] duration-300 ease-out hover:-translate-y-2 hover:shadow-xl hover:shadow-teal-900/10 ${highlight ? "border-teal-700 ring-1 ring-teal-700" : "border-slate-200"} bg-white`}
                  >
                    <h3 className="font-semibold text-slate-900">{p.label}</h3>
                    <p className="mt-4 text-2xl font-semibold text-teal-700">
                      {isFree ? "₹0" : `₹${p.price_monthly.toLocaleString("en-IN")}/month`}
                    </p>
                    {isFree ? (
                      <p className="text-xs text-slate-500">Free</p>
                    ) : (
                      <p className="text-xs text-slate-500">or ₹{p.price_yearly.toLocaleString("en-IN")}/year</p>
                    )}

                    <div className="mt-5 space-y-3 border-t border-slate-100 pt-5" data-testid={`pricing-limits-${p.plan_code}`}>
                      <div className="flex items-start gap-2 text-sm text-slate-700">
                        <CalendarCheck className="mt-0.5 h-4 w-4 shrink-0 text-teal-700" />
                        <span>Up to {p.monthly_limit} appointments/month</span>
                      </div>
                      <div className="flex items-start gap-2 text-sm text-slate-700">
                        <Users className="mt-0.5 h-4 w-4 shrink-0 text-teal-700" />
                        <span>{p.max_staff} Active Staff Account{p.max_staff > 1 ? "s" : ""}</span>
                      </div>
                    </div>

                    <div className="mt-4 flex items-start gap-2 rounded-lg bg-teal-50 px-3 py-2.5 text-xs font-medium text-teal-800">
                      <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0 text-teal-700" />
                      <span>All platform features included — zero feature-gating</span>
                    </div>

                    <Link to="/contact-sales" className="mt-6">
                      <Button variant={highlight ? "default" : "outline"} className={`w-full ${highlight ? "bg-teal-700 hover:bg-teal-800" : "border-teal-700 text-teal-700"}`}>
                        {isFree ? "Start Trial" : "Book Demo"}
                      </Button>
                    </Link>
                  </div>
                );
              })
            )}
          </div>
          <p className="mt-6 text-center text-xs text-slate-400">No online payment option. Our team will help you get started.</p>

          <div className="mx-auto mt-16 max-w-4xl">
            <h3 className="text-center text-2xl font-semibold text-slate-900">Core Platform Features — Included in All Tiers</h3>
            <p className="mx-auto mt-3 max-w-2xl text-center text-sm text-slate-600">
              Every subscription plan grants complete access to the entire RootLyst ecosystem with zero feature-gating.
            </p>
            <div className="mt-8 grid grid-cols-1 gap-4 sm:grid-cols-2" data-testid="core-features-list">
              {CORE_FEATURES.map((f) => (
                <div key={f.title} className="card-surface card-surface-hover flex gap-3 rounded-xl border border-slate-200 bg-slate-50 p-4">
                  <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-teal-50 text-teal-700">
                    <f.icon className="h-4 w-4" />
                  </div>
                  <div>
                    <p className="font-medium text-slate-900">{f.title}</p>
                    <p className="mt-1 text-sm text-slate-600">{f.desc}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      <SiteFooter />
    </div>
  );
}
